import React, { useEffect, useState } from 'react';
import { App as CapacitorApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { useAppConfig } from '../contexts/AppConfigContext';
import { isNative } from '../services/platformService';
import SafeIcon from '../common/SafeIcon';
import { FiDownload, FiRefreshCw } from 'react-icons/fi';
import {
  APP_STORE_DEEP_LINK as appStoreDeepLink,
  APP_STORE_WEB_URL as appStoreWebLink,
} from '../constants/app';

// Compare two semver strings ("1.2.3"). Returns -1 if a<b, 0 if equal, 1 if a>b.
// Treats missing segments as 0 ("1.2" -> "1.2.0").
const compareSemver = (a, b) => {
  const parse = (s) => String(s || '0').split('.').map(n => parseInt(n, 10) || 0);
  const av = parse(a);
  const bv = parse(b);
  const len = Math.max(av.length, bv.length);
  for (let i = 0; i < len; i++) {
    const x = av[i] || 0;
    const y = bv[i] || 0;
    if (x < y) return -1;
    if (x > y) return 1;
  }
  return 0;
};

export default function ForceUpdateGate({ children }) {
  const { config, refresh } = useAppConfig();
  const [deviceVersion, setDeviceVersion] = useState(null);
  const [resolved, setResolved] = useState(false);

  useEffect(() => {
    if (!isNative()) {
      setResolved(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const info = await CapacitorApp.getInfo();
        if (!cancelled) setDeviceVersion(info?.version || '0.0.0');
      } catch {
        if (!cancelled) setDeviceVersion('0.0.0');
      } finally {
        if (!cancelled) setResolved(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Web - never gates.
  if (!isNative()) return children;
  // Wait for device version + config before deciding.
  if (!resolved || !config) return children;

  const floor = config.min_ios_app_version || '0.0.0';
  if (compareSemver(deviceVersion, floor) >= 0) return children;

  const handleUpdate = async () => {
    const url = appStoreDeepLink || appStoreWebLink;
    try {
      await Browser.open({ url });
    } catch {
      window.location.href = url;
    }
  };

  return (
    <div className="min-h-screen bg-app flex items-center justify-center px-6">
      <div className="bg-card border border-border-subtle rounded-2xl shadow-card max-w-md w-full p-8 text-center">
        <div className="w-14 h-14 mx-auto mb-5 bg-accent/10 rounded-2xl flex items-center justify-center">
          <SafeIcon icon={FiDownload} className="w-7 h-7 text-accent" />
        </div>
        <h1 className="text-2xl font-bold text-text-primary mb-3">Update required</h1>
        <p className="text-text-secondary leading-relaxed mb-6 whitespace-pre-line">
          {config.force_update_message || 'A new version of TeeSync is available. Please update to continue.'}
        </p>
        <p className="text-xs text-text-muted mb-6">
          You're on {deviceVersion || '-'}. Minimum required: {floor}.
        </p>
        <button
          type="button"
          onClick={handleUpdate}
          className="w-full px-4 py-3 rounded-xl bg-accent text-white font-semibold hover:bg-interactive-hover transition-colors mb-3"
        >
          Update now
        </button>
        <button
          type="button"
          onClick={() => refresh()}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm text-text-secondary hover:text-text-primary"
        >
          <SafeIcon icon={FiRefreshCw} className="w-4 h-4" />
          Check again
        </button>
      </div>
    </div>
  );
}
